import { HiOutlineDocumentText } from "react-icons/hi";
import { TbBuildingBank } from "react-icons/tb";
import { Building2 } from "lucide-react";
import { parseApplicationType } from "../../Reducer/ApplicationTypeReducer";
import {
  ClientOverviewSection,
  OverviewCard,
} from "./overview.types";

interface OverviewCardEnrichment {
  Icon: OverviewCard["Icon"];
  description: string;
  path: string;
  disabled: boolean;
}

const defaultCardEnrichment: OverviewCardEnrichment = {
  Icon: HiOutlineDocumentText,
  description: "",
  path: "/processing",
  disabled: true,
};

// Frontend-only fields the API does not send. Looked up by section key.
const overviewCardEnrichment: Record<string, OverviewCardEnrichment> = {
  costs: {
    Icon: HiOutlineDocumentText,
    description:
      "Manage invoices and receipts through Processing, Inbox, To Review, Ready, Export and Archive stages.",
    path: "/processing",
    disabled: false,
  },
  supplierStatements: {
    Icon: Building2,
    description:
      "Review supplier statements and match them against recorded supplier invoices.",
    path: "/processing",
    disabled: false,
  },
  bankStatements: {
    Icon: TbBuildingBank,
    description:
      "Upload and reconcile bank statements through Processing, Inbox and Archive stages.",
    path: "/",
    disabled: false,
  },
};

const COSTS_STATUS_KEYS = new Set(["processing", "toreview", "ready"]);
const SUPPLIER_STATEMENT_STATUS_KEYS = new Set([
  "processing",
  "toreview",
  "ready",
]);
const BANK_STAT_LABELS: Record<string, string> = {
  collected: "Collected",
  processed: "Processed",
  transactions: "Transactions",
};

const normalizeStatKey = (key: string) =>
  key.toLowerCase().replace(/[^a-z]/g, "");

/**
 * API section -> Overview card.
 *
 * 1. Read the backend section (label, applicationType, stats).
 * 2. Resolve it to an app ApplicationType.
 * 3. Attach icon / description / path / disabled from the local lookup.
 * 4. Rename API `encryptedId` to `encryptedKey` for the app/Redux.
 * 5. Split stats: `total` becomes the card count, while status rows map
 *    `value` -> `count` (Costs and Supplier Statements show workflow stages;
 *    Bank Statements show collection, processing, and transaction counts).
 */
export const mapClientOverviewSectionToCard = (
  section: ClientOverviewSection,
): OverviewCard => {
  // 1 + 2. Prefer applicationType.key, fall back to section.
  const applicationType =
    parseApplicationType(section.applicationType?.key) ??
    parseApplicationType(section.section);

  // 3. Known section first, then known application type, else a disabled default.
  const enrichment =
    overviewCardEnrichment[section.section] ??
    (applicationType ? overviewCardEnrichment[applicationType] : undefined) ??
    defaultCardEnrichment;

  // 4a. Status rows for the card. Skip `total` — that is the header count.
  const stats = (section.stats ?? [])
    .filter((stat) => {
      if (stat.key === "total") return false;
      const normalizedKey = normalizeStatKey(stat.key);
      if (applicationType === "costs") {
        return COSTS_STATUS_KEYS.has(normalizedKey);
      }
      if (applicationType === "supplierStatements") {
        return SUPPLIER_STATEMENT_STATUS_KEYS.has(normalizedKey);
      }
      if (applicationType === "bankStatements") {
        return normalizedKey in BANK_STAT_LABELS;
      }
      return true;
    })
    .map((stat) => {
      const normalizedKey = normalizeStatKey(stat.key);
      return {
        key: stat.key,
        label:
          applicationType === "bankStatements"
            ? BANK_STAT_LABELS[normalizedKey]
            : stat.label,
        count: stat.value ?? 0,
      };
    });

  // 4b. Card item count. If `total` is missing, sum the remaining stats.
  const totalItems =
    section.stats?.find((stat) => stat.key === "total")?.value ??
    stats.reduce((sum, stat) => sum + stat.count, 0);

  return {
    value: applicationType,
    // API field is `encryptedId`; we expose it as `encryptedKey` for the app.
    encryptedKey: section.applicationType?.encryptedId ?? "",
    section: section.section,
    title: section.label,
    Icon: enrichment.Icon,
    description: enrichment.description,
    path: enrichment.path,
    totalItems,
    stats,
    disabled: enrichment.disabled || !applicationType,
  };
};

// Service entry: one API array item becomes one card, in the same order.
export const mapClientOverviewToCards = (
  sections: ClientOverviewSection[],
): OverviewCard[] => sections.map(mapClientOverviewSectionToCard);
